/*---------------------------------------------------------------
 ?? Project: Ring2All Platform
 ????? Author: Rodrigo Cuadra
 ?? Company: Ring2All LLC
 ?? File: components\BackupJobActionBar.tsx
 ??? Created: 2026-05-02
 ?? Description:
    Implementation of the maintenance module.
----------------------------------------------------------------*/


import { Play, Terminal, Copy } from 'lucide-react';
import { FixedActionBar } from '../../../../components/ui/FixedActionBar';
import SimpleTooltip from '../../../../components/ui/SimpleTooltip';
import { useNotifications } from '../../../../contexts/NotificationContext';
import { useI18n } from '../../../../hooks/useI18n';

interface Props {
    jobId?: number | string;
    isSaving: boolean;
    onSave: (action: string) => void;
    onCancel: () => void;
    onRun?: (id: number | string) => void;
}

export function BackupJobActionBar({ jobId, isSaving, onSave, onCancel, onRun }: Props) {
    const { t } = useI18n();
    const { showSuccess: notifySuccess, showError: notifyError } = useNotifications();

    const handleCopyCommand = async () => {
        const cmd = `r2a_cmd make-backup ${jobId}`;
        try {
            await navigator.clipboard.writeText(cmd);
            notifySuccess(t('common.copiedToClipboard'));
        } catch {
            // Fallback for older browsers
            const ta = document.createElement('textarea');
            ta.value = cmd;
            document.body.appendChild(ta);
            ta.focus(); ta.select();
            try { document.execCommand('copy'); notifySuccess(t('common.copiedToClipboard')); }
            catch { notifyError('Failed to copy'); }
            document.body.removeChild(ta);
        }
    };

    return (
        <FixedActionBar
            onSaveWithAction={onSave}
            onCancel={onCancel}
            isSaving={isSaving}
            isEditMode={!!jobId}
            cancelText={t('common.cancel')}
            useSaveDropdown
            saveDropdownStorageKey="backup-jobs:saveAction"
            additionalButtons={[]}
            saveTooltip={t('backupRestore:tooltips.save')}
            cancelTooltip={t('backupRestore:tooltips.cancel')}
        >
            {!!jobId && onRun && (
                <div className="flex items-center gap-4">
                    <SimpleTooltip content={t('backupRestore:tooltips.runNow')}>
                        <button
                            type="button" onClick={() => onRun(jobId)}
                            className="flex items-center gap-2 px-3 py-1 bg-emerald-100 hover:bg-emerald-200 dark:bg-emerald-900/30 dark:hover:bg-emerald-800/50 text-emerald-700 dark:text-emerald-400 rounded-md text-sm font-medium transition-colors border border-emerald-200 dark:border-emerald-800"
                        >
                            <Play className="h-4 w-4" />{t('backupRestore:actions.run')}
                        </button>
                    </SimpleTooltip>

                    <SimpleTooltip content={t('backupRestore:tooltips.commandPreview')}>
                        <div className="flex items-center gap-3 bg-muted/50 px-3 py-1.5 rounded-md border border-border">
                            <div className="flex items-center gap-2">
                                <Terminal className="h-3.5 w-3.5 text-muted-foreground" />
                                <code className="text-xs font-mono text-muted-foreground">r2a_cmd make-backup {jobId}</code>
                            </div>
                            <div className="h-4 w-px bg-border mx-1" />
                            <button
                                type="button" onClick={handleCopyCommand}
                                className="flex items-center gap-1.5 text-xs text-muted-foreground hover:text-primary transition-colors hover:bg-background/80 p-1 rounded cursor-pointer"
                                title={t('common.copy')}
                            >
                                <Copy className="h-3 w-3" />
                                <span className="sr-only">{t('common.copy')}</span>
                            </button>
                        </div>
                    </SimpleTooltip>
                </div>
            )}
        </FixedActionBar>
    );
}
